// 观点与证据（lingzhi-studio 的 /studio/api/insights）。和灵智原生同源、同一个登录令牌。
import { request } from './client'

const BASE = '/studio/api/insights'

export type ClaimKind = 'fact' | 'opinion' | 'inference'
export const CLAIM_KIND_LABEL: Record<ClaimKind, string> = { fact: '事实', opinion: '作者观点', inference: '推断' }

export interface Claim {
  id: string
  knowledgeId: string
  kbId: string
  docTitle: string
  chunkId: string
  chunkIndex: number
  text: string
  quote: string
  kind: ClaimKind
  condition: string
  concepts: string[]
  speaker: string | null
  /** ai：「整理观点」整理的（老数据没有这个字段）；user：读的时候自己划下的 */
  source?: 'ai' | 'user'
  createdAt: string
}
export type JobStatus = 'running' | 'done' | 'failed' | 'cancelled' | 'interrupted'
export interface Job {
  id: string
  knowledgeId: string
  kbId: string
  docTitle: string
  status: JobStatus
  total: number
  done: number
  claims: number
  dropped: number
  failedChunks: number
  error?: string
  createdAt: string
  updatedAt: string
}
export interface DocInsights {
  doc: { knowledgeId: string; kbId: string; title: string }
  totalChunks: number
  processedChunks: number
  claims: Claim[]
  job: Job | null
}
export interface Passage {
  id: string
  knowledgeId: string
  kbId: string
  title: string
  text: string
  score: number
}
export interface EvidenceRef {
  kind: 'passage' | 'claim'
  id: string
  knowledgeId: string
  kbId: string
  title: string
  text: string
}
export interface Judgment {
  id: string
  topic: string
  reasoning: string
  evidence: EvidenceRef[]
  source: 'manual' | 'think'
  thinkId?: string
  feedback: { text: string; at: string }[]
  saved?: { knowledgeId: string; kbId: string; title: string }
  saveError?: string
  createdAt: string
}
export type RelationType = 'SUPPORTS' | 'CHALLENGES' | 'REVISES' | 'DEPENDS_ON' | 'APPLIED_TO'
export const RELATION_LABEL: Record<RelationType, string> = { SUPPORTS: '支持', CHALLENGES: '质疑', REVISES: '修正', DEPENDS_ON: '依赖', APPLIED_TO: '用于' }
export interface NodeRef {
  kind: 'claim' | 'passage' | 'judgment'
  id: string
  label: string
  knowledgeId?: string
  kbId?: string
}
export interface Relation {
  id: string
  source: NodeRef
  target: NodeRef
  type: RelationType
  reason: string
  createdAt: string
}
export interface Topic {
  name: string
  claims: number
  docs: number
}
export interface Meta {
  limits: { callsPerDay: number; defaultChunks: number; maxChunks: number }
  usedToday: number
  remainingToday: number
  folder: string
}

const data = <T>(p: Promise<{ data: T }>) => p.then((r) => r.data)
const enc = encodeURIComponent

export const insightsMeta = () => data(request<{ data: Meta }>(`${BASE}/meta`))
export const insightDocs = () => data(request<{ data: { knowledgeId: string; kbId: string; title: string; claims: number; lastAt: string }[] }>(`${BASE}/docs`))
export const docInsights = (knowledgeId: string) => data(request<{ data: DocInsights }>(`${BASE}/docs/${enc(knowledgeId)}`))
export const extractClaims = (knowledgeId: string, maxChunks?: number) => data(request<{ data: Job }>(`${BASE}/docs/${enc(knowledgeId)}/extract`, { body: maxChunks ? { maxChunks } : {} }))
export const getJob = (id: string) => data(request<{ data: Job }>(`${BASE}/jobs/${id}`))
export const cancelJob = (id: string) => data(request<{ data: Job }>(`${BASE}/jobs/${id}/cancel`, { body: {} }))
/** 记为观点：阅读时选中的一句（不调用模型）。同一篇里同一句只记一次。 */
export const markClaim = (input: { knowledgeId: string; quote: string; note?: string; kind?: ClaimKind }) => data(request<{ data: Claim }>(`${BASE}/claims`, { body: input }))
export const unmarkClaim = (id: string) => request(`${BASE}/claims/${enc(id)}`, { method: 'DELETE' })
/** 全部观点（最新的在前）。 */
export const listClaims = () => data(request<{ data: Claim[] }>(`${BASE}/claims`))
/** 合并相近的观点：把 ids 并进 keepId 这一条（只限同一篇）。 */
export const mergeClaims = (keepId: string, ids: string[]) => data(request<{ data: { claim: Claim; merged: number; relations: number } }>(`${BASE}/claims/${enc(keepId)}/merge`, { body: { ids } }))
export const searchEvidence = (query: string) => data(request<{ data: { passages: Passage[]; claims: Claim[]; judgments: Judgment[] } }>(`${BASE}/search`, { body: { query } }))
export const listTopics = () => data(request<{ data: Topic[] }>(`${BASE}/topics`))
export const getTopic = (name: string) => data(request<{ data: { name: string; claims: Claim[]; docs: { knowledgeId: string; kbId: string; title: string; claims: number }[]; relations: Relation[] } }>(`${BASE}/topics/${enc(name)}`))
export const listJudgments = () => data(request<{ data: Judgment[] }>(`${BASE}/judgments`))
export const getJudgment = (id: string) => data(request<{ data: Judgment & { relations: Relation[] } }>(`${BASE}/judgments/${id}`))
export const createJudgment = (input: { topic: string; reasoning: string; evidence: Pick<EvidenceRef, 'kind' | 'id' | 'knowledgeId' | 'kbId' | 'title' | 'text'>[] }) =>
  data(request<{ data: Judgment }>(`${BASE}/judgments`, { body: input }))
/** 删掉一条判断：关系一起删，知识库「思考」里为它存的那一篇也删掉。 */
export const deleteJudgment = (id: string) => request(`${BASE}/judgments/${id}`, { method: 'DELETE' })
export const addFeedback = (id: string, text: string) => data(request<{ data: Judgment }>(`${BASE}/judgments/${id}/feedback`, { body: { text } }))
export const syncJudgment = (id: string) => data(request<{ data: Judgment }>(`${BASE}/judgments/${id}/sync`, { body: {} }))
export const listRelations = (node?: { kind: string; id: string }) => data(request<{ data: Relation[] }>(`${BASE}/relations`, { query: node ? { kind: node.kind, id: node.id } : undefined }))
export const relate = (input: { source: Omit<NodeRef, 'label'> & { label?: string }; target: Omit<NodeRef, 'label'> & { label?: string }; type: RelationType; reason: string }) =>
  data(request<{ data: Relation }>(`${BASE}/relations`, { body: input }))
export const unrelate = (id: string) => request(`${BASE}/relations/${id}`, { method: 'DELETE' })

/** 「已选的依据」里的一条：原文片段、观点或判断。 */
export type Picked =
  | { kind: 'passage'; id: string; knowledgeId: string; kbId: string; title: string; text: string }
  | { kind: 'claim'; id: string; knowledgeId: string; kbId: string; title: string; text: string }
  | { kind: 'judgment'; id: string; title: string; text: string }

export const pickPassage = (p: Passage): Picked => ({ kind: 'passage', id: p.id, knowledgeId: p.knowledgeId, kbId: p.kbId, title: p.title, text: p.text })
export const pickClaim = (c: Claim): Picked => ({ kind: 'claim', id: c.id, knowledgeId: c.knowledgeId, kbId: c.kbId, title: c.docTitle, text: c.text })
export const pickJudgment = (j: Judgment): Picked => ({ kind: 'judgment', id: j.id, title: j.topic, text: j.reasoning })

/** 关系的一端（给服务端）。 */
export const nodeOf = (p: Picked) => (p.kind === 'passage' ? { kind: p.kind, id: p.id, knowledgeId: p.knowledgeId, kbId: p.kbId, label: p.text } : { kind: p.kind, id: p.id })
